import { useCallback, useEffect, useMemo, useState } from 'react';
import Hammer from 'react-hammerjs';
import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'hammerjs';

import {
  DROP_ITEM_SIZE,
  InteractiveSequenceAttempt,
  ViewProps,
} from '../settings/interactive-response.constants';
import { InteractiveDrag } from '../../../shared/interactive-drag-drop/interactive-drag';
import { isEveryDraggableDropped } from '../../../shared/interactive-drag-drop/interactive-drag-drop.utils';
import {
  CANCELLED,
  INTERACTIVE_CONTAINER_ID,
  PLACED,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import InteractiveDragDrop from '../../../shared/interactive-drag-drop/interactive-drag-drop';
import { useInteractiveResponsePlay } from './interactive-response-sequence.hooks';
import * as Styled from './interactive-response-sequence.style';
import {
  InteractiveResponseItemCount,
  InteractiveResponseScreenReader,
  InteractiveResponseTitle,
} from '../../shared/interactive-response-shared';
import { useTileScroll } from '../../shared/interactive-response-shared.hooks';
import {
  InteractiveCanvas,
  InteractiveResponseModalControlsWrapper,
  InteractiveResponsePlayContainer,
  InteractiveResponseScrollableList,
  InteractiveResponseScrollableWrapper,
} from '../../shared/interactive-response-shared.style';
import { ChangeAnswerSmall } from '../../shared/interactive-response-check-answer';
import {
  initialise,
  populateAnswers,
} from './interactive-response-sequence.utils';
import { InteractiveSequenceDrop } from './interactive-sequence-drop';
import { returnItemToOriginalPosition } from '../../shared/interactive-response-shared.utils';
import {
  InteractiveResponseHelpControls,
  InteractiveResponseNavMobileControls,
} from '../../shared/interactive-response-controls';
import { FlexLeftAlign } from 'approot/shared/styles/flex.style';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import {
  InteractiveAudioProvider,
  useInteractiveAudioContext,
} from '../../shared/interactive-audio.context';

const InteractiveSequenceSmallFree = ({
  properties,
  playClick,
  playStart,
  playAudio,
  onAnswerAttempt,
  attemptAnswers,
  toggleAudio,
}: ViewProps & {
  onClose: () => void;
  playClick: () => void;
  playStart: () => void;
  toggleAudio: () => void;
  playAudio: boolean;
  onAnswerAttempt: InteractiveSequenceAttempt;
  attemptAnswers?: InteractiveResponseAnswerFragment;
}) => {
  const [scrollRef, setScrollRef] = useState<HTMLElement | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [checkFreeResponseAnswer, setCheckFreeResponseAnswer] =
    useState<boolean>(false);

  const sequenceProperties = properties.interactiveResponse;

  const { setAudioList } = useInteractiveAudioContext();

  const {
    currentSlide,
    prevDisabled,
    nextDisabled,
    handleNext,
    handlePrev,
    reset,
  } = useTileScroll({
    containerRef: scrollRef,
    itemWidth: 122,
    slideCount: sequenceProperties.items.length,
  });

  const {
    playAttempt,
    setPlayAttempt,
    dropItems,
    setDropItems,
    dragItems,
    setDragItems,
    keyboardState,
    handleOnDrop,
    handleKeyboardInteraction,
  } = useInteractiveResponsePlay({
    playClick,
    isCheckAnswerAtEnd: true,
    isFreeResponse: true,
    onAnswerAttempt: () => {
      setCheckFreeResponseAnswer(true);
    },
  });

  const initialiseItems = useCallback(
    (startAgain?: boolean) => {
      const { dragItems, dropItems } =
        attemptAnswers && !startAgain
          ? populateAnswers(
              attemptAnswers,
              sequenceProperties.items,
              sequenceProperties.variant
            )
          : initialise(
              sequenceProperties.variant,
              sequenceProperties.items,
              true
            );
      setDropItems(dropItems);
      setDragItems(dragItems);
      setPlayAttempt(playAttempt + 1);
      reset();
    },
    [
      sequenceProperties.variant,
      sequenceProperties.items,
      playAttempt,
      setPlayAttempt,
      setDragItems,
      setDropItems,
      reset,
      attemptAnswers,
    ]
  );

  const setup = useCallback(
    (startAgain?: boolean) => {
      setTimeout(() => {
        initialiseItems(startAgain);
        playStart();
      }, 100);
    },
    [initialiseItems, playStart]
  );

  const isEverythingAnswered = useMemo(() => {
    return isEveryDraggableDropped(dragItems);
  }, [dragItems]);

  const isInSubmittedState = isEverythingAnswered && !checkFreeResponseAnswer;

  const keyboardProps = {
    target:
      keyboardState?.action === CANCELLED || keyboardState?.action === PLACED
        ? undefined
        : keyboardState?.id,
    onKeyboardInteraction: handleKeyboardInteraction,
  };

  useEffect(() => {
    initialiseItems();
    //eslint-disable-next-line
  }, [properties, attemptAnswers]);

  useEffect(() => {
    setAudioList(
      sequenceProperties.items.map(item => ({
        itemId: item.itemId,
        audioUrl: item.audio?.original,
      }))
    );
  }, [setAudioList, sequenceProperties.items]);

  return (
    <InteractiveResponsePlayContainer>
      <InteractiveCanvas
        id={INTERACTIVE_CONTAINER_ID}
        data-selector="interactive-sequence-window"
      >
        <FlexLeftAlign $marginBottom={8}>
          <InteractiveResponseHelpControls
            howToPlay={sequenceProperties.howToPlay}
            toggleAudio={toggleAudio}
            playAudio={playAudio}
          />
        </FlexLeftAlign>
        <InteractiveResponseTitle
          title={sequenceProperties.title}
          subTitle={sequenceProperties.subTitle}
        />

        <Hammer
          options={{
            recognizers: {
              swipe: { enable: true },
            },
          }}
          onSwipe={arg => {
            if (arg.direction === DIRECTION_LEFT && !prevDisabled) {
              handlePrev();
            } else if (arg.direction === DIRECTION_RIGHT && !nextDisabled) {
              handleNext();
            }
          }}
        >
          <InteractiveResponseScrollableWrapper>
            <InteractiveResponseScrollableList ref={setScrollRef}>
              {dragItems.map((dragItem, index) => (
                <Styled.InteractiveResponseScrollableItem
                  id={dragItem.id}
                  key={`${playAttempt}-${dragItem.id}-${index}-${playAudio}`}
                  $isInFocus={currentSlide === index}
                  $index={index}
                  data-selector={`sequence-image-${index}`}
                >
                  {!dragItem.isDropped && (
                    <InteractiveDrag
                      id={dragItem.id}
                      imageUrl={dragItem.question.image?.small}
                      isFit={dragItem.question.image?.isFit}
                      description={dragItem.question.image?.description || ''}
                      baseColour={dragItem.question.baseColour}
                      variant={dragItem.question.variant}
                      text={dragItem.question.text}
                      richtext={dragItem.question.richtext}
                      audioUrl={dragItem.question.audioUrl}
                      keyboard={keyboardProps}
                    />
                  )}
                </Styled.InteractiveResponseScrollableItem>
              ))}
            </InteractiveResponseScrollableList>
          </InteractiveResponseScrollableWrapper>
        </Hammer>

        <InteractiveResponseItemCount
          index={currentSlide}
          total={sequenceProperties.items.length}
        />

        {isInSubmittedState && (
          <ChangeAnswerSmall
            isActive={isActive}
            setIsActive={setIsActive}
            moreInformation={properties.interactiveResponse.moreInformation}
            onStartOver={() => {
              setup(true);
            }}
          />
        )}

        <Styled.InteractiveResponseDropItemContainer>
          {dropItems.map(dropItem => {
            const zone = dropItem.zones[0];
            const droppedDragItem = dragItems.find(
              d => d.id === zone.dragItemId || d.id === zone.previewItemId
            );

            return (
              <InteractiveSequenceDrop
                key={`${playAttempt}-${dropItem.id}-${playAudio}`}
                id={dropItem.id}
                label={dropItem.label}
                labelLength={sequenceProperties.labelLength}
                labelBorder={sequenceProperties.labelBorder}
                isCorrect={undefined}
                dropItems={dropItems}
                dragItems={dragItems}
                onDrop={(destinationId, sourceId) => {
                  handleOnDrop(destinationId, sourceId);
                }}
                isContinuousMarking={false}
                onIncorrectAnswer={element =>
                  returnItemToOriginalPosition({
                    element,
                    droppedDragItemId: droppedDragItem?.id || '',
                    scale: 1,
                    dropItem,
                    dropItems,
                    dragItems,
                    setDragItems,
                    setDropItems,
                  })
                }
              >
                {droppedDragItem && (
                  <InteractiveDrag
                    id={droppedDragItem.id}
                    imageUrl={droppedDragItem.question.image?.small}
                    isFit={droppedDragItem.question.image?.isFit}
                    description={
                      droppedDragItem.question.image?.description || ''
                    }
                    keyboard={keyboardProps}
                    baseColour={droppedDragItem.question.baseColour}
                    variant={droppedDragItem.question.variant}
                    text={droppedDragItem.question.text}
                    richtext={droppedDragItem.question.richtext}
                    audioUrl={droppedDragItem.question.audioUrl}
                    isDisabled={zone.isCorrect !== undefined}
                    isCorrect={undefined}
                    droppedAt={zone.dragItemId ? dropItem.label : ''}
                    overAt={zone.previewItemId ? dropItem.label : ''}
                    scaleTo={DROP_ITEM_SIZE}
                  />
                )}
              </InteractiveSequenceDrop>
            );
          })}
        </Styled.InteractiveResponseDropItemContainer>

        <InteractiveResponseScreenReader keyboardState={keyboardState} />
      </InteractiveCanvas>
      <InteractiveResponseModalControlsWrapper>
        <InteractiveResponseNavMobileControls
          onNext={handleNext}
          onPrevious={handlePrev}
          buttonLabel="Submit Answer"
          onCheckAnswer={() => {
            setCheckFreeResponseAnswer(false);
            onAnswerAttempt(dropItems, dragItems);
          }}
          canCheckAnswer={checkFreeResponseAnswer}
          isNextDisabled={nextDisabled}
          isPrevDisabled={prevDisabled}
        />
      </InteractiveResponseModalControlsWrapper>
    </InteractiveResponsePlayContainer>
  );
};

const InteractiveSequenceSmallFreeWrapper = (
  props: ViewProps & {
    onClose: () => void;
    playClick: () => void;
    playStart: () => void;
    playAudio: boolean;
    toggleAudio: () => void;
    attemptAnswers?: InteractiveResponseAnswerFragment;
    onAnswerAttempt: InteractiveSequenceAttempt;
  }
) => {
  return (
    <InteractiveAudioProvider>
      <InteractiveDragDrop>
        <InteractiveSequenceSmallFree {...props} />
      </InteractiveDragDrop>
    </InteractiveAudioProvider>
  );
};

export default InteractiveSequenceSmallFreeWrapper;
